// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.FilterItem.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

import React, { PureComponent } from 'react';
import typeof { DOMElement } from 'react';
import PropTypes from 'prop-types';
import connect from 'share/enhancers/Izenda.Connect';
import Validator from 'share/components/hoc/Izenda.Validator';
import { observable } from 'mobx';
import MenuContext from 'share/components/controls/Izenda.MenuContext';
import GridLayout from 'share/components/controls/Izenda.GridLayout';
import actions from './Izenda.FilterItemActions';
import FilterPopover from './Izenda.FilterPopover';
import FilterItemDetails from './Izenda.FilterItemDetails';

const RIGHT_MOUSE_BUTTON = 2;

const ValidatorFilterItemDetails = Validator()(FilterItemDetails);

@connect(
  { toClass: true },
  actions
)
export default class FilterItem extends PureComponent {
  static propTypes = {
    store: PropTypes.object.isRequired,
    itemData: PropTypes.object.isRequired,
    showPosition: PropTypes.bool.isRequired
  };

  // Indicates if filter is in filter alias edit mode
  @observable isEditMode: boolean = false;
  // Indicate if the popover is visible
  @observable visible: boolean = false;
  // Indicate if the context menu is visible
  @observable isMenuContextVisible: boolean = false;

  /**
   * Set filter DOM
   */
  setFilterDom = (domElement: DOMElement) => {
    this.props.itemData.filterItemDom = domElement;
  };

  /**
   * Enable edit mode
   */
  enableEditMode = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();

    this.selectFilter();
    this.isEditMode = true;
    this.visible = false;

    const { validation } = this.props;
    validation.reset();
  };

  /**
   * Disable edit mode
   */
  disableEditMode = () => {
    this.isEditMode = false;
  };

  /**
   * Render delete icon in context menu
   */
  renderDeleteIcon() {
    const {
      localization,
      itemData: { isParameter },
      removeFilterItem
    } = this.props;
    return (
      <a
        className='btn'
        title={localization.getText('COMMON_LABEL_DELETE')}
        data-btnName='btnAction'
        onClick={removeFilterItem}
        disabled={isParameter}
      >
        <i className='icon-trash-empty' />
        <span>{localization.getText('COMMON_LABEL_DELETE')}</span>
      </a>
    );
  }

  /**
   * Render edit icon in context menu
   */
  renderEditIcon() {
    const { localization, openFilterPropertiesPopup } = this.props;

    return (
      <a
        className='btn'
        title={localization.getText('COMMON_LABEL_EDIT')}
        data-btnName='btnAction'
        onClick={openFilterPropertiesPopup}
      >
        <i className='icon-setting' />
        <span>{localization.getText('COMMON_LABEL_EDIT')}</span>
      </a>
    );
  }

  /**
   * Render context menu
   */
  renderContextMenu() {
    const {
      itemData: { isParameter, isForKeyJoin },
      checkValidFilter
    } = this.props;
    const isValidFilter = checkValidFilter();
    const showEdit = !isForKeyJoin && isValidFilter;
    const showDelete = !isParameter && !isForKeyJoin;
    const showDivider = showEdit && showDelete;
    if (!showEdit && !showDelete)
      return null;
    return (
      <div className='izenda-FilterItemPill_ContextMenu'>
        <ul className='izenda-ContextMenu-list'>
          {showEdit     && <li> {this.renderEditIcon()} </li>}
          {showDivider  && <li className='izenda-ContextMenu-divider' />}
          {showDelete   && <li> {this.renderDeleteIcon()} </li>}
        </ul>
      </div>
    );
  }

  /**
   * On click on filter
   */
  onClickFilter = () => {
    this.selectFilter();
    if (!this.isEditMode) this.visible = true;
  };

  /**
   * Select current filter item as selected filter
   */
  selectFilter() {
    const {
      store,
      itemData,
      store: { reportFilters }
    } = this.props;
    reportFilters.setConfigModeToField(itemData);
    store.getOperatorValueSource(reportFilters.configField);
  }

  /**
   * On mouse down filter
   */
  onMouseDownFilter = (e: Event) => {
    if (e.button === RIGHT_MOUSE_BUTTON) {
      this.selectFilter();
      this.visible = false;
    }
  };

  /**
   * Close popover
   */
  closePopover = () => {
    this.visible = false;
    return Promise.resolve();
  };

  /**
   * When the context menu change state
   */
  toggleMenuContext = (value: boolean) => {
    this.isMenuContextVisible = value;
  };

  render() {
    const {
      itemData,
      itemData: { uniqueId, filterItemDom, isParameter, isForKeyJoin },
      store: {
        reportFilters: { configField },
        reportFilters
      },
      openFilterPropertiesPopup,
      removeFilterItem,
      store,
      showPosition,
      checkValidFilter
    } = this.props;
    const isValidFilter = checkValidFilter();
    return (
      <GridLayout.Col className='izenda-FilterItemPill u_noPaddingAll' cols='3' draggable={!this.isEditMode}>
        <MenuContext
          onMenuContextChanged={this.toggleMenuContext}
          menuComponent={this.renderContextMenu()}
          id={uniqueId}
          isDisabled={this.isEditMode}
        >
          <div
            className='filter-wrapper'
            onClick={this.onClickFilter}
            onMouseDown={this.onMouseDownFilter}
            onTouchStart={this.onClickFilter}
            ref={this.setFilterDom}
          >
            <ValidatorFilterItemDetails
              itemData={itemData}
              store={store}
              showPosition={showPosition}
              disableEditMode={this.disableEditMode}
              enableEditMode={this.enableEditMode}
              visible={this.visible}
              isEditMode={this.isEditMode}
              isMenuContextVisible={this.isMenuContextVisible}
              field={`Filters${uniqueId}`}
            />
            {isValidFilter && this.visible && configField.fieldDetails === itemData && (
              <FilterPopover
                reportFilters={reportFilters}
                closePopover={this.closePopover}
                store={store}
                anchor={filterItemDom}
                configField={configField}
                openFilterPropertiesPopup={openFilterPropertiesPopup}
                removeFilterItem={removeFilterItem}
                showDeleteButton={!isParameter && !isForKeyJoin}
                showSettingsButton={!isForKeyJoin}
              />
            )}
          </div>
        </MenuContext>
      </GridLayout.Col>
    );
  }
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/Filter/Izenda.FilterItem.js